import React, { useState } from "react";
import { Box, Typography, Input, Button, FormControl, FormLabel, Select, Option } from "@mui/joy";
import { useAssignments } from "../../../contexts/controllers/useAssignments";
import AssignmentCard from "../display/AssignmentsCard";
import { AssignmentAcceptanceStatus } from "../../../api/assignments/Assignments.codes";

type SearchType = "adjustoId" | "externalId" | "claimId";

const AssignmentSearch: React.FC = () => {
  const { searchAssignment } = useAssignments();
  const [searchType, setSearchType] = useState<SearchType>("adjustoId");
  const [searchValue, setSearchValue] = useState("");
  const [searchResult, setSearchResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setError("Veuillez saisir une valeur de recherche");
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResult(null);

    try {
      const searchParams = {
        [searchType]: searchValue,
      };

      const result = await searchAssignment(searchParams);
      if (result) {
        setSearchResult(result.response);
      }
    } catch (err) {
      setError("Erreur lors de la recherche");
    } finally {
      setLoading(false);
    }
  };

  const handleActionComplete = async () => {
    if (searchResult) {
      handleSearch();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 2,
          backgroundColor: "background.surface",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: "clamp(160px, 30vw, 240px)" }}>
            <FormLabel>Type de recherche</FormLabel>
            <Select
              value={searchType}
              onChange={(_, newValue) => newValue && setSearchType(newValue as SearchType)}
            >
              <Option value="adjustoId">Adjusto ID</Option>
              <Option value="externalId">External ID</Option>
              <Option value="claimId">Claim ID</Option>
            </Select>
          </FormControl>

          <FormControl sx={{ flexGrow: 1 }}>
            <FormLabel>Valeur</FormLabel>
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={`Saisir ${searchType}`}
              error={!!error}
            />
          </FormControl>

          <Button onClick={handleSearch} loading={loading} sx={{ mt: "auto" }}>
            Rechercher
          </Button>
        </Box>

        {error && (
          <Typography level="body-sm" color="danger" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          backgroundColor: "background.body",
          p: 2,
        }}
      >
        {searchResult && (
          <AssignmentCard
            assignment={searchResult}
            showAcceptanceActions={
              !searchResult.acceptanceStatus ||
              searchResult.acceptanceStatus === AssignmentAcceptanceStatus.UNAVAILABLE
            }
            editMode={searchResult.acceptanceStatus === AssignmentAcceptanceStatus.ACCEPTED}
            onActionComplete={handleActionComplete}
          />
        )}
      </Box>
    </Box>
  );
};

export default AssignmentSearch;
